import { useAuth } from '../context/AuthContext';
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api from '../api';
import Pagination, { PAGE_SIZE } from '../components/Pagination';
import { Plus, Search, Edit2, Trash2, Users, X, Download, History, CheckCircle, Bus, MapPin, Calendar, Package, AlertCircle, UserCheck } from 'lucide-react';

const emptyForm = { nome: '', telefone: '', email: '', documento: '', tipo: 'PF', observacoes: '' };

/**
 * Auto-formata CPF → 413.433.258.38  (todos os separadores são pontos)
 * Auto-formata CNPJ → 12.345.678/0001-99 (formato padrão)
 */
function formatarDocumento(v) {
  const n = v.replace(/\D/g, '');
  if (n.length <= 11) {
    // CPF: 413.433.258.38
    if (n.length <= 3) return n;
    if (n.length <= 6) return `${n.slice(0,3)}.${n.slice(3)}`;
    if (n.length <= 9) return `${n.slice(0,3)}.${n.slice(3,6)}.${n.slice(6)}`;
    return `${n.slice(0,3)}.${n.slice(3,6)}.${n.slice(6,9)}.${n.slice(9,11)}`;
  }
  // CNPJ: 12.345.678/0001-99
  const d = n.slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0,2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
}

const statusCls = {
  confirmada: 'bg-emerald-100 text-emerald-700',
  pendente: 'bg-amber-100 text-amber-700',
  cancelada: 'bg-red-100 text-red-700',
};
const statusLabel = { confirmada: 'Confirmada', pendente: 'Pendente', cancelada: 'Cancelada' };

function Badge({ tipo }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tipo === 'PJ' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
      {tipo === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'}
    </span>
  );
}

// ─── Modal Histórico do Cliente ──────────────────────────────────────────────
function HistoricoModal({ clienteId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/clientes/${clienteId}/historico`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Erro ao carregar histórico'))
      .finally(() => setLoading(false));
  }, [clienteId]);

  const fmt = (v) => v?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) ?? '0,00';
  const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="historico-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
              <History className="w-4 h-4 text-orange-600" />
            </div>
            <div>
              <h2 className="font-bold text-slate-800 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Histórico de Reservas
              </h2>
              {data && <p className="text-slate-500 text-xs">{data.cliente.nome}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Carregando histórico...</div>
        ) : !data ? (
          <div className="p-10 text-center text-slate-400 text-sm">Erro ao carregar dados</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Resumo */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="bg-slate-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>{data.resumo.total}</p>
                <p className="text-xs text-slate-500 mt-0.5">Reservas</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{data.resumo.confirmadas}</p>
                <p className="text-xs text-emerald-600 mt-0.5">Confirmadas</p>
              </div>
              <div className="bg-red-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Manrope, sans-serif' }}>{data.resumo.canceladas}</p>
                <p className="text-xs text-red-500 mt-0.5">Canceladas</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-blue-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{data.resumo.checkins_realizados}</p>
                <p className="text-xs text-blue-600 mt-0.5">Embarques</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-3 text-center col-span-2 sm:col-span-1">
                <p className="text-lg font-bold text-orange-700" style={{ fontFamily: 'Manrope, sans-serif' }}>R$ {fmt(data.resumo.total_gasto)}</p>
                <p className="text-xs text-orange-600 mt-0.5">Total gasto</p>
              </div>
            </div>

            {/* Encomendas do cliente */}
            {data.encomendas?.length > 0 && (
              <div>
                <h3 className="font-bold text-slate-700 text-sm mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4 text-slate-400" />
                  Encomendas ({data.resumo.total_encomendas}) — {data.resumo.encomendas_entregues} entregues
                </h3>
                <div className="space-y-2">
                  {data.encomendas.map(e => {
                    const dt = e.viagem?.data ? new Date(e.viagem.data + 'T00:00:00').toLocaleDateString('pt-BR') : '—';
                    const isRem = e.eh_remetente;
                    const sMap = { recebida: 'bg-blue-100 text-blue-700', em_transito: 'bg-amber-100 text-amber-700', entregue: 'bg-emerald-100 text-emerald-700', cancelada: 'bg-red-100 text-red-700' };
                    const sLabel = { recebida: 'Recebida', em_transito: 'Em Trânsito', entregue: 'Entregue', cancelada: 'Cancelada' };
                    return (
                      <div key={e.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                        <Package className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 text-sm">{e.codigo_interno}</p>
                          <p className="text-xs text-slate-500">{isRem ? 'Remetente' : 'Destinatário'} · {e.viagem?.titulo || '—'} · {dt}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${sMap[e.status] || 'bg-slate-100 text-slate-700'}`}>
                          {sLabel[e.status] || e.status}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lista de reservas */}
            {data.reservas.length === 0 ? (
              <div className="flex flex-col items-center py-10 text-slate-400">
                <Bus className="w-10 h-10 mb-2 opacity-20" />
                <p className="text-sm">Nenhuma reserva encontrada</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {data.reservas.map((r) => (
                  <div key={r.id} data-testid={`historico-item-${r.id}`}
                    className="bg-white border border-slate-200 rounded-xl p-4 hover:border-orange-200 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      {/* Assento */}
                      <div className="w-11 h-11 rounded-xl bg-orange-500 flex flex-col items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg leading-none">{r.assento}</span>
                        <span className="text-orange-200 text-[9px] leading-none">ASSENTO</span>
                      </div>
                      {/* Info viagem */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 text-sm truncate">
                          {r.viagem?.titulo || 'Viagem não encontrada'}
                        </p>
                        {r.viagem && (
                          <>
                            <div className="flex items-center gap-1 text-slate-500 text-xs mt-0.5">
                              <MapPin className="w-3 h-3 text-orange-400 flex-shrink-0" />
                              <span>{r.viagem.origem} → {r.viagem.destino}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              <div className="flex items-center gap-1 text-slate-400 text-xs">
                                <Calendar className="w-3 h-3" />
                                <span>{fmtDate(r.viagem.data)}</span>
                                {r.viagem.horario && <span className="text-orange-500 font-medium">· {r.viagem.horario}</span>}
                              </div>
                            </div>
                          </>
                        )}
                        {r.observacoes && (
                          <p className="text-slate-400 text-xs mt-1 italic truncate">{r.observacoes}</p>
                        )}
                      </div>
                      {/* Status + valor */}
                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCls[r.status] || 'bg-slate-100 text-slate-700'}`}>
                          {statusLabel[r.status] || r.status}
                        </span>
                        {r.valor && (
                          <span className="text-sm font-semibold text-slate-700">R$ {fmt(r.valor)}</span>
                        )}
                        {r.check_in && (
                          <span className="flex items-center gap-1 text-emerald-600 text-xs font-medium">
                            <CheckCircle className="w-3 h-3" /> Embarcou
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Clientes() {
  const { user } = useAuth();
  const canWrite = user?.role !== 'financeiro';
  const [clientes, setClientes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [historicoId, setHistoricoId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [cpfDuplicado, setCpfDuplicado] = useState(null);   // null | { nome, telefone }
  const [checkingCpf, setCheckingCpf] = useState(false);
  const cpfCheckRef = useRef(null);

  // Ao fechar o modal, limpa o estado de duplicação
  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setCpfDuplicado(null);
    setCheckingCpf(false);
  };

  // Verificação de CPF duplicado (onBlur no campo documento)
  const handleDocumentoBlur = async () => {
    const doc = (form.documento || '').trim();
    // Verifica por contagem de dígitos (não comprimento da string formatada)
    const digits = doc.replace(/\D/g, '');
    if (!digits || digits.length < 11) {
      setCpfDuplicado(null);
      return;
    }
    // Cancela timeout anterior se houver
    if (cpfCheckRef.current) clearTimeout(cpfCheckRef.current);
    setCheckingCpf(true);
    try {
      const params = { documento: doc };
      if (editingId) params.excluir_id = editingId;
      // Timeout de 6s para não travar o botão caso a VPS esteja lenta
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000);
      cpfCheckRef.current = timeoutId;
      const { data } = await api.get('/clientes/verificar-documento', {
        params,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      setCpfDuplicado(data.existe ? data.cliente : null);
    } catch (err) {
      // Em caso de timeout ou erro de rede, permite o cadastro (backend valida novamente)
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        console.warn('Verificação de CPF cancelada por timeout — prosseguindo');
      }
      setCpfDuplicado(null);
    } finally {
      cpfCheckRef.current = null;
      setCheckingCpf(false);
    }
  };

  // Formata e limpa aviso ao digitar CPF/CNPJ
  const handleDocumentoChange = (val) => {
    setForm(prev => ({ ...prev, documento: formatarDocumento(val) }));
    if (cpfDuplicado) setCpfDuplicado(null);
  };

  const fetchClientes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/clientes', {
        params: { search: appliedSearch || undefined, tipo: tipoFilter || undefined, skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE },
      });
      const count = Number(res.headers['x-total-count']) || 0;
      // Excluiu o último item da última página: volta uma página
      if (res.data.length === 0 && page > 1 && count > 0) { setPage(Math.max(1, Math.ceil(count / PAGE_SIZE))); return; }
      setClientes(res.data);
      setTotal(count);
    } catch { toast.error('Erro ao carregar clientes'); }
    finally { setLoading(false); }
  }, [appliedSearch, tipoFilter, page]);

  useEffect(() => { fetchClientes(); }, [fetchClientes]);

  // Buscar: aplica o texto digitado e volta para a página 1 (se nada mudou, só recarrega)
  const submitSearch = () => {
    if (search === appliedSearch && page === 1) fetchClientes();
    else { setAppliedSearch(search); setPage(1); }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setCpfDuplicado(null);
    setCheckingCpf(false);
    setModalOpen(true);
  };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ ...c, documento: formatarDocumento(c.documento || '') });
    setCpfDuplicado(null);
    setCheckingCpf(false);
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/clientes/${editingId}`, form);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await api.post('/clientes', form);
        toast.success('Cliente criado com sucesso!');
      }
      closeModal();
      fetchClientes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar cliente');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/clientes/${deleteId}`);
      toast.success('Cliente excluído com sucesso!');
      setDeleteId(null);
      fetchClientes();
    } catch { toast.error('Erro ao excluir cliente'); }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = { search: search || undefined, tipo: tipoFilter || undefined };
      const response = await api.get('/clientes/export/csv', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clientes.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exportado com sucesso!');
    } catch { toast.error('Erro ao exportar CSV'); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-5 page-enter" data-testid="clientes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Clientes</h1>
          <p className="text-slate-500 text-sm mt-0.5">{total} cliente(s) cadastrado(s)</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExportCSV} disabled={exporting} data-testid="btn-export-clientes-csv"
            className="flex items-center gap-2 border border-slate-300 text-slate-600 hover:bg-slate-50 font-medium px-4 py-2.5 rounded-lg transition-colors text-sm disabled:opacity-60">
            <Download className="w-4 h-4" /> {exporting ? 'Exportando...' : 'Exportar CSV'}
          </button>
          {canWrite && (
          <button onClick={openCreate} data-testid="btn-novo-cliente"
            className="flex items-center gap-2 bg-[#d72d21] hover:bg-[#b91c1c] text-white font-medium px-4 py-2.5 rounded-lg transition-colors active:scale-95 text-sm">
            <Plus className="w-4 h-4" /> Novo Cliente
          </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitSearch()}
              placeholder="Buscar por nome, email, CPF/CNPJ, telefone..."
              data-testid="clientes-search-input"
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]"
            />
          </div>
          <select
            value={tipoFilter}
            onChange={(e) => { setTipoFilter(e.target.value); setPage(1); }}
            data-testid="clientes-tipo-filter"
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 bg-white"
          >
            <option value="">Todos os tipos</option>
            <option value="PF">Pessoa Física</option>
            <option value="PJ">Pessoa Jurídica</option>
          </select>
          <button onClick={submitSearch} data-testid="btn-buscar-clientes"
            className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-medium transition-colors">
            Buscar
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Carregando...</div>
        ) : clientes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Users className="w-12 h-12 mb-3 opacity-20" />
            <p className="font-medium">Nenhum cliente encontrado</p>
            <p className="text-xs mt-1">Clique em "Novo Cliente" para começar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Nome</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tipo</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Telefone</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Email</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Documento</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {clientes.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`cliente-row-${c.id}`}>
                    <td className="px-5 py-3.5 font-medium text-slate-800">{c.nome}</td>
                    <td className="px-5 py-3.5"><Badge tipo={c.tipo} /></td>
                    <td className="px-5 py-3.5 text-slate-500 hidden md:table-cell">{c.telefone || '—'}</td>
                    <td className="px-5 py-3.5 text-slate-500 hidden lg:table-cell">{c.email || '—'}</td>
                    <td className="px-5 py-3.5 text-slate-500 hidden lg:table-cell">{c.documento || '—'}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setHistoricoId(c.id)} data-testid={`btn-historico-cliente-${c.id}`}
                          title="Histórico de reservas"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-orange-600 hover:bg-orange-50 transition-colors">
                          <History className="w-4 h-4" />
                        </button>
                        <button onClick={() => openEdit(c)} data-testid={`btn-edit-cliente-${c.id}`}
                          className={`p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors ${!canWrite ? 'hidden' : ''}`}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteId(c.id)} data-testid={`btn-delete-cliente-${c.id}`}
                          className={`p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors ${!canWrite ? 'hidden' : ''}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} total={total} onChange={setPage} disabled={loading} />
      </div>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cliente-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingId ? 'Editar Cliente' : 'Novo Cliente'}
              </h2>
              <button onClick={closeModal} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Nome completo *</label>
                <input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required
                  data-testid="cliente-nome-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Telefone</label>
                  <input value={form.telefone || ''} onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                    placeholder="(11) 99999-9999" data-testid="cliente-telefone-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Tipo</label>
                  <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                    data-testid="cliente-tipo-select"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 bg-white">
                    <option value="PF">Pessoa Física</option>
                    <option value="PJ">Pessoa Jurídica</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Email</label>
                <input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="cliente-email-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">CPF / CNPJ</label>
                <div className="relative">
                  <input
                    value={form.documento || ''}
                    onChange={(e) => handleDocumentoChange(e.target.value)}
                    onBlur={handleDocumentoBlur}
                    data-testid="cliente-documento-input"
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors pr-9 ${
                      cpfDuplicado
                        ? 'border-red-400 focus:ring-red-200 bg-red-50'
                        : 'border-slate-300 focus:ring-[#0147a0]/20 focus:border-[#0147a0]'
                    }`}
                  />
                  {/* Ícone de status no campo */}
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                    {checkingCpf && (
                      <div className="w-4 h-4 border-2 border-[#0147a0] border-t-transparent rounded-full animate-spin" />
                    )}
                    {!checkingCpf && cpfDuplicado && (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                    {!checkingCpf && !cpfDuplicado && form.documento?.length >= 11 && (
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                    )}
                  </div>
                </div>

                {/* Aviso de CPF duplicado */}
                {cpfDuplicado && (
                  <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-3" data-testid="cpf-duplicado-aviso">
                    <div className="flex items-start gap-2.5">
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-red-700">
                          Este CPF já está cadastrado no sistema.
                        </p>
                        <div className="mt-2 flex items-center gap-2 bg-white rounded-lg border border-red-100 px-3 py-2">
                          <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                            <UserCheck className="w-3.5 h-3.5 text-red-600" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-800">{cpfDuplicado.nome}</p>
                            {cpfDuplicado.telefone && (
                              <p className="text-xs text-slate-500">{cpfDuplicado.telefone}</p>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-red-500 mt-1.5">
                          Altere o CPF ou localize o cliente existente para realizar a reserva.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Observações</label>
                <textarea value={form.observacoes || ''} onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                  rows={3} data-testid="cliente-obs-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-[#F0F4FF]/60 transition-colors">
                  Cancelar
                </button>
                <button type="submit" disabled={saving || !!cpfDuplicado || checkingCpf} data-testid="btn-salvar-cliente"
                  className="flex-1 bg-[#d72d21] hover:bg-[#b91c1c] text-white rounded-lg text-sm font-semibold py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {checkingCpf ? 'Verificando CPF...' : saving ? 'Salvando...' : (editingId ? 'Atualizar' : 'Cadastrar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Histórico Modal */}
      {historicoId && <HistoricoModal clienteId={historicoId} onClose={() => setHistoricoId(null)} />}

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-bold text-slate-800 text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Confirmar exclusão</h3>
            <p className="text-slate-500 text-sm mb-5">Esta ação não pode ser desfeita. Deseja realmente excluir este cliente?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} data-testid="btn-cancel-delete"
                className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">
                Cancelar
              </button>
              <button onClick={handleDelete} data-testid="btn-confirm-delete"
                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold py-2.5">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
